import * as React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch } from "react-redux";

import {
  getMyPortalQuery,
  getMyPortalAnalytics,
  updateMyPortalObject,
} from "../../redux/dynamo_db/actions";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";

function getUserInfo() {
  try {
    return extractUserInfoFromToken(getLocalStorageSAMLToken());
  } catch (e) {
    return { username: "anonymous", email: "" };
  }
}

// Guided access patterns per table. Each maps to a (table, index) pair the
// my_portal_query lambda validates; the browser only supplies values. `pkLabel`
// / `skLabel` document the underlying key attribute for the selected pattern.
const ACCESS_PATTERNS = {
  objects: [
    {
      key: "objects#base",
      label: "By object id (PK / SK)",
      index: "",
      pkLabel: "Object id (PK)",
      skAttr: "SK",
      skLabel: "SK (e.g. METADATA or TAXONOMY#…)",
    },
    {
      key: "objects#inverse",
      label: "Inverse lookup (SK → PK)",
      index: "InverseLookup",
      pkLabel: "SK value (e.g. an alias, or TAXONOMY#…)",
      skAttr: "PK",
      skLabel: "PK filter (optional)",
    },
    {
      key: "objects#parent",
      label: "By parent (ParentIndex)",
      index: "ParentIndex",
      pkLabel: "Parent id (GSI1PK)",
      skAttr: "GSI1SK",
      skLabel: "Child id (GSI1SK) filter (optional)",
    },
  ],
  "events-raw": [
    {
      key: "events#base",
      label: "By event id (PK)",
      index: "",
      pkLabel: "Event id (PK)",
      skAttr: null,
    },
    {
      key: "events#objectdate",
      label: "By object id + date (ObjectIdDateIndex)",
      index: "ObjectIdDateIndex",
      pkLabel: "Object id (GSI2PK)",
      skAttr: "GSI2SK",
      skLabel: "Date (GSI2SK, YYYY-MM-DD)",
    },
    {
      key: "events#datedeleted",
      label: "By date / deleted (DateDeletedIndex)",
      index: "DateDeletedIndex",
      pkLabel: "GSI1PK value",
      skAttr: "GSI1SK",
      skLabel: "GSI1SK filter (optional)",
    },
  ],
  analytics: [
    {
      key: "analytics#base",
      label: "By partition (PK / SK)",
      index: "",
      pkLabel: "Partition (PK, e.g. PAGE_VIEW#LIFETIME)",
      skAttr: "SK",
      skLabel: "SK filter (optional)",
    },
  ],
};

const SK_OPERATORS = [
  { value: "eq", label: "equals" },
  { value: "begins_with", label: "begins with" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "between", label: "between" },
];

// Static pages live in the analytics table under PAGE_VIEW#<period>. LIFETIME is
// the cumulative partition, which is the natural source list for aliasing.
const STATIC_PAGES_PARTITION = "PAGE_VIEW#LIFETIME";
// DynamoDB can't order by a non-key attribute, so we pull the max page (the
// lambda caps at MAX_LIMIT=500) and sort by view count client-side. When the
// result hits this cap there are more pages than shown (see staticPagesTruncated).
const STATIC_PAGES_LIMIT = 500;

function columnsFromItems(items) {
  // Union of keys, with PK/SK first for readability.
  const seen = new Set();
  const ordered = [];
  ["PK", "SK", "path", "alias"].forEach((k) => {
    if (items.some((i) => k in i)) {
      seen.add(k);
      ordered.push(k);
    }
  });
  items.forEach((item) => {
    Object.keys(item).forEach((k) => {
      if (!seen.has(k)) {
        seen.add(k);
        ordered.push(k);
      }
    });
  });
  return ordered;
}

function renderCell(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function MyPortal() {
  const dispatch = useDispatch();
  const [tab, setTab] = React.useState(0);

  // ---- Query tab state -----------------------------------------------------
  const [table, setTable] = React.useState("objects");
  const [patternKey, setPatternKey] = React.useState(ACCESS_PATTERNS.objects[0].key);
  const [pk, setPk] = React.useState("");
  const [skOp, setSkOp] = React.useState("");
  const [sk, setSk] = React.useState("");
  const [sk2, setSk2] = React.useState("");
  const [limit, setLimit] = React.useState(100);
  const [queryItems, setQueryItems] = React.useState([]);
  const [queryNext, setQueryNext] = React.useState(null);
  const [queryRunning, setQueryRunning] = React.useState(false);
  const [queryError, setQueryError] = React.useState(null);
  const [queried, setQueried] = React.useState(false);

  const patterns = ACCESS_PATTERNS[table];
  const pattern = patterns.find((p) => p.key === patternKey) || patterns[0];

  const handleSelectTable = (value) => {
    setTable(value);
    setPatternKey(ACCESS_PATTERNS[value][0].key);
    setSkOp("");
    setSk("");
    setSk2("");
  };

  const buildParams = (cursor) => {
    const params = { table, index: pattern.index, pk: pk.trim(), limit };
    if (pattern.skAttr && skOp) {
      params.sk_op = skOp;
      params.sk = sk;
      if (skOp === "between") params.sk2 = sk2;
    }
    if (cursor) params.next = cursor;
    return params;
  };

  const runQuery = async (cursor) => {
    if (!pk.trim()) {
      setQueryError("A partition key value is required.");
      return;
    }
    setQueryRunning(true);
    setQueryError(null);
    const acted = await dispatch(getMyPortalQuery(buildParams(cursor)));
    setQueryRunning(false);
    if (acted?.errors) {
      setQueryError(acted.errors.error || `Request failed (${acted.errors.responseCode}).`);
      return;
    }
    const res = acted?.myPortalQueryResult || {};
    const items = res.items || [];
    setQueryItems(cursor ? (prev) => prev.concat(items) : items);
    setQueryNext(res.next || null);
    setQueried(true);
  };

  const presetStaticPages = () => {
    handleSelectTable("analytics");
    setPk(STATIC_PAGES_PARTITION);
    setSkOp("");
    setSk("");
  };

  const queryColumns = React.useMemo(() => columnsFromItems(queryItems), [queryItems]);

  const downloadQuery = (format) => {
    if (!queryItems.length) return;
    const fields = queryColumns.map((c) => ({ name: c }));
    const fname = `my-portal-${table}`;
    if (format === "csv") downloadCSV(fields, fname, ",", queryItems);
    else downloadJSON(fields, fname, queryItems);
  };

  // ---- Analytics tab state -------------------------------------------------
  const [objectId, setObjectId] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [analytics, setAnalytics] = React.useState(null);
  const [analyticsRunning, setAnalyticsRunning] = React.useState(false);
  const [analyticsError, setAnalyticsError] = React.useState(null);

  const runAnalytics = async () => {
    if (!objectId.trim()) {
      setAnalyticsError("An object id is required.");
      return;
    }
    setAnalyticsRunning(true);
    setAnalyticsError(null);
    const acted = await dispatch(
      getMyPortalAnalytics(objectId.trim(), dateFrom || undefined, dateTo || undefined),
    );
    setAnalyticsRunning(false);
    if (acted?.errors) {
      setAnalyticsError(
        acted.errors.error || `Request failed (${acted.errors.responseCode}).`,
      );
      setAnalytics(null);
      return;
    }
    setAnalytics(acted?.myPortalAnalytics || null);
  };

  // ---- Aliases tab state ---------------------------------------------------
  // Each entry is { path, count }, ordered by count desc.
  const [staticPages, setStaticPages] = React.useState([]);
  const [staticPagesLoading, setStaticPagesLoading] = React.useState(false);
  const [staticPagesLoaded, setStaticPagesLoaded] = React.useState(false);
  const [staticPagesTruncated, setStaticPagesTruncated] = React.useState(false);
  const [staticPagesError, setStaticPagesError] = React.useState(null);
  const [aliasList, setAliasList] = React.useState([]);
  const [aliasPath, setAliasPath] = React.useState("");
  const [aliasName, setAliasName] = React.useState("");
  const [aliasLoading, setAliasLoading] = React.useState(false);
  const [aliasSaving, setAliasSaving] = React.useState(false);
  const [aliasError, setAliasError] = React.useState(null);
  const [aliasSuccess, setAliasSuccess] = React.useState(null);

  const loadStaticPages = React.useCallback(async () => {
    setStaticPagesLoading(true);
    setStaticPagesError(null);
    const acted = await dispatch(
      getMyPortalQuery({
        table: "analytics",
        pk: STATIC_PAGES_PARTITION,
        limit: STATIC_PAGES_LIMIT,
      }),
    );
    setStaticPagesLoading(false);
    setStaticPagesLoaded(true);
    if (acted?.errors) {
      // Surface the failure instead of masking it as "No static pages found":
      // the request can fail on the browser path (auth/CORS/network through API
      // Gateway) even though the data and lambda are healthy.
      setStaticPagesError(
        acted.errors.error ||
          `Could not load static pages (${acted.errors.responseCode}).`,
      );
      setStaticPages([]);
      setStaticPagesTruncated(false);
      return;
    }
    const items = acted?.myPortalQueryResult?.items || [];
    // Order by view count, most-viewed first (DynamoDB returns them in SK order).
    const pages = items
      .filter((i) => i.SK)
      .map((i) => ({ path: i.SK, count: Number(i.count) || 0 }))
      .sort((a, b) => b.count - a.count);
    setStaticPages(pages);
    // Hitting the limit means the partition has more pages than we fetched, so
    // the list below is only the first STATIC_PAGES_LIMIT (not the top by count).
    setStaticPagesTruncated(items.length >= STATIC_PAGES_LIMIT);
  }, [dispatch]);

  const loadAliases = React.useCallback(async () => {
    setAliasLoading(true);
    setAliasError(null);
    const acted = await dispatch(
      getMyPortalQuery({ table: "objects", mode: "aliases", limit: 500 }),
    );
    setAliasLoading(false);
    if (acted?.errors) {
      setAliasError(acted.errors.error || `Request failed (${acted.errors.responseCode}).`);
      return;
    }
    setAliasList(acted?.myPortalQueryResult?.items || []);
  }, [dispatch]);

  const handleTabChange = (_e, value) => {
    setTab(value);
    if (value === 2 && !staticPages.length) {
      loadStaticPages();
      loadAliases();
    }
  };

  const saveAlias = async () => {
    if (!aliasPath.trim() || !aliasName.trim()) {
      setAliasError("Both a static page path and an alias are required.");
      return;
    }
    setAliasSaving(true);
    setAliasError(null);
    setAliasSuccess(null);
    const { username } = getUserInfo();
    const acted = await dispatch(
      updateMyPortalObject(aliasPath.trim(), aliasName.trim(), {}, username),
    );
    setAliasSaving(false);
    if (acted?.errors) {
      setAliasError(acted.errors.error || `Save failed (${acted.errors.responseCode}).`);
      return;
    }
    setAliasSuccess(`Alias saved for ${aliasPath.trim()}.`);
    setAliasName("");
    loadAliases();
  };

  const refreshAliasesTab = () => {
    loadStaticPages();
    loadAliases();
  };

  // path -> existing alias, so the static-page browser can flag which pages are
  // already aliased and show the current alias inline.
  const aliasByPath = React.useMemo(() => {
    const map = {};
    aliasList.forEach((row) => {
      if (row.path) map[row.path] = row.alias;
    });
    return map;
  }, [aliasList]);

  return (
    <Box sx={{ p: 2 }}>
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Query" />
        <Tab label="Analytics" />
        <Tab label="Aliases" />
      </Tabs>

      {/* ---------------------------------------------------------------- Query */}
      {tab === 0 && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Query the three My Portal tables through their indexes and GSIs.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Table</InputLabel>
              <Select
                label="Table"
                value={table}
                onChange={(e) => handleSelectTable(e.target.value)}
              >
                <MenuItem value="objects">my-portal-objects</MenuItem>
                <MenuItem value="events-raw">my-portal-events-raw</MenuItem>
                <MenuItem value="analytics">my-portal-analytics</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 280 }} size="small">
              <InputLabel>Access pattern</InputLabel>
              <Select
                label="Access pattern"
                value={patternKey}
                onChange={(e) => setPatternKey(e.target.value)}
              >
                {patterns.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" onClick={presetStaticPages}>
              Preset: static pages
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <TextField
              size="small"
              label={pattern.pkLabel}
              value={pk}
              onChange={(e) => setPk(e.target.value)}
              sx={{ minWidth: 360 }}
            />
            {pattern.skAttr && (
              <>
                <FormControl sx={{ minWidth: 140 }} size="small">
                  <InputLabel>Sort key</InputLabel>
                  <Select
                    label="Sort key"
                    value={skOp}
                    onChange={(e) => setSkOp(e.target.value)}
                  >
                    <MenuItem value="">(none)</MenuItem>
                    {SK_OPERATORS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {skOp && (
                  <TextField
                    size="small"
                    label={pattern.skLabel || "SK value"}
                    value={sk}
                    onChange={(e) => setSk(e.target.value)}
                    sx={{ minWidth: 240 }}
                  />
                )}
                {skOp === "between" && (
                  <TextField
                    size="small"
                    label="SK upper bound"
                    value={sk2}
                    onChange={(e) => setSk2(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                )}
              </>
            )}
            <TextField
              size="small"
              type="number"
              label="Limit"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 100)}
              sx={{ width: 110 }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={queryRunning}
              onClick={() => runQuery(null)}
            >
              Run query
            </Button>
            <Button
              startIcon={<FileDownloadIcon />}
              disabled={!queryItems.length}
              onClick={() => downloadQuery("csv")}
            >
              CSV
            </Button>
            <Button
              startIcon={<FileDownloadIcon />}
              disabled={!queryItems.length}
              onClick={() => downloadQuery("json")}
            >
              JSON
            </Button>
          </Box>

          {queryRunning && <LinearProgress sx={{ mb: 2 }} />}
          {queryError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {queryError}
            </Alert>
          )}
          {queried && !queryError && (
            <Chip
              label={`${queryItems.length} item(s)${queryNext ? " — more available" : ""}`}
              sx={{ mb: 1 }}
            />
          )}
          {queryItems.length > 0 && (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {queryColumns.map((c) => (
                      <TableCell key={c}>
                        <strong>{c}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queryItems.map((item, idx) => (
                    <TableRow key={idx}>
                      {queryColumns.map((c) => (
                        <TableCell key={c}>{renderCell(item[c])}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {queryNext && (
            <Button sx={{ mt: 2 }} onClick={() => runQuery(queryNext)} disabled={queryRunning}>
              Load more
            </Button>
          )}
        </Box>
      )}

      {/* ------------------------------------------------------------ Analytics */}
      {tab === 1 && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Aggregated view / download analytics for a portal object (served by
            the dlapi-v2 analytics endpoint).
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            <TextField
              size="small"
              label="Object id (ObjectId__c)"
              value={objectId}
              onChange={(e) => setObjectId(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={analyticsRunning}
              onClick={runAnalytics}
            >
              Aggregate
            </Button>
          </Box>
          {analyticsRunning && <LinearProgress sx={{ mb: 2 }} />}
          {analyticsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {analyticsError}
            </Alert>
          )}
          {analytics && (
            <Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Chip color="primary" label={`Views: ${analytics.views ?? 0}`} />
                <Chip color="primary" label={`Downloads: ${analytics.downloads ?? 0}`} />
                <Chip label={`Total events: ${analytics.total_events ?? 0}`} />
                <Chip label={`Last view: ${analytics.last_view || "—"}`} />
                <Chip label={`Last download: ${analytics.last_download || "—"}`} />
              </Box>
              {["by_country", "by_browser_language"].map((section) => (
                <Box key={section} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {section === "by_country" ? "By country" : "By browser language"}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>{section === "by_country" ? "Country" : "Language"}</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Views</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Downloads</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(analytics[section] || []).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.country || row.language}</TableCell>
                          <TableCell>{row.views}</TableCell>
                          <TableCell>{row.downloads}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* --------------------------------------------------------------- Aliases */}
      {tab === 2 && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Map a static page path to a human-readable alias. The path list comes
            from the analytics table (PAGE_VIEW). Saving stamps updated_at /
            updated_by automatically.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
            <Autocomplete
              freeSolo
              options={staticPages.map((p) => p.path)}
              value={aliasPath}
              onChange={(_e, value) => setAliasPath(value || "")}
              onInputChange={(_e, value) => setAliasPath(value || "")}
              sx={{ minWidth: 420 }}
              renderInput={(p) => (
                <TextField {...p} size="small" label="Static page path (PK)" />
              )}
            />
            <TextField
              size="small"
              label="Alias (SK)"
              value={aliasName}
              onChange={(e) => setAliasName(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={aliasSaving}
              onClick={saveAlias}
            >
              Save alias
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={refreshAliasesTab}
              disabled={aliasLoading || staticPagesLoading}
            >
              Refresh
            </Button>
          </Box>
          {(aliasSaving || aliasLoading || staticPagesLoading) && (
            <LinearProgress sx={{ mb: 2 }} />
          )}
          {aliasError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {aliasError}
            </Alert>
          )}
          {aliasSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {aliasSuccess}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Available static pages
            {staticPages.length > 0 && ` (${staticPages.length})`}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            All static page paths seen in the analytics table ({STATIC_PAGES_PARTITION}),
            ordered by view count (most viewed first). Pick one to pre-fill the form above.
          </Typography>
          {staticPagesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {staticPagesError}
            </Alert>
          )}
          {staticPagesTruncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Showing the first {STATIC_PAGES_LIMIT} pages only — the limit was
              reached, so not all static pages are displayed (and these are not
              necessarily the top {STATIC_PAGES_LIMIT} by count).
            </Alert>
          )}
          <Box sx={{ overflowX: "auto", mb: 3, maxHeight: 360, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Static page path</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Views</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Current alias</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {staticPages.map(({ path, count }) => (
                  <TableRow key={path} hover>
                    <TableCell>{path}</TableCell>
                    <TableCell align="right">{count.toLocaleString()}</TableCell>
                    <TableCell>
                      {aliasByPath[path] ? (
                        <Chip size="small" color="primary" label={aliasByPath[path]} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setAliasPath(path);
                          setAliasName(aliasByPath[path] || "");
                        }}
                      >
                        {aliasByPath[path] ? "Edit" : "Add alias"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!staticPages.length &&
                  !staticPagesLoading &&
                  staticPagesLoaded &&
                  !staticPagesError && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                        No static pages found.
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Existing aliases
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Path (PK)</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Alias (SK)</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Updated at</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Updated by</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {aliasList.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.path}</TableCell>
                    <TableCell>{row.alias}</TableCell>
                    <TableCell>{row.updated_at || "—"}</TableCell>
                    <TableCell>{row.updated_by || "—"}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setAliasPath(row.path);
                          setAliasName(row.alias);
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Box>
  );
}
